import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI, clientsAPI } from '../../api/client';
import { formatPhoneInput } from '../../lib/validators';
import UserAvatar from '../../components/UserAvatar';
import { ArrowLeft, Save, Eye, EyeOff, RefreshCw, Camera, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'super_admin',    label: 'Super Admin',        desc: 'Control total del sistema' },
  { value: 'lider_proyecto', label: 'Líder de Proyecto',  desc: 'Gestión y aprobación de evaluaciones' },
  { value: 'levantador',     label: 'Levantador',          desc: 'Inspección de campo y checklists' },
  { value: 'cliente',        label: 'Cliente',             desc: 'Solo lectura de su empresa' },
];
const BLOOD_TYPES = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
const STATUS_OPTIONS = ['activo','inactivo','suspendido','temporal'];
const STATUS_LABELS = { activo: 'Activo', inactivo: 'Inactivo', suspendido: 'Suspendido', temporal: 'Temporal' };

function SectionTitle({ n, children, collapsible, open, onToggle }) {
  return (
    <div className="flex items-center gap-3 mb-4" onClick={collapsible ? onToggle : undefined} style={collapsible ? { cursor: 'pointer' } : {}}>
      <div className="w-6 h-6 bg-soem-primary text-white rounded-full flex items-center justify-center text-xs font-bold">{n}</div>
      <h3 className="font-bold text-soem-navy flex-1">{children}</h3>
      {collapsible && (open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />)}
      {!collapsible && <div className="flex-1 h-px bg-slate-200" />}
    </div>
  );
}

function generatePassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

export default function UserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const photoRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [supervisorResults, setSupervisorResults] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [medicalOpen, setMedicalOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  // #A11 — se necesita el status ORIGINAL (no solo 'activo' hardcoded)
  // para saber si realmente cambió y decidir si hay que llamar
  // changeStatus al guardar.
  const [originalStatus, setOriginalStatus] = useState(null);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    role: 'levantador', client_id: '', status: 'activo',
    temporary_access_expires_at: '', password: '',
    emergency_contact: { name: '', relationship: '', phone: '', email: '' },
    social_security: { imss_number: '', blood_type: '', allergies: '', medical_notes: '' },
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setEC = (k, v) => setForm(f => ({ ...f, emergency_contact: { ...f.emergency_contact, [k]: v } }));
  const setSS = (k, v) => setForm(f => ({ ...f, social_security: { ...f.social_security, [k]: v } }));
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  useEffect(() => {
    Promise.all([clientsAPI.list(), usersAPI.list()]).then(([c, u]) => {
      setClients(c.data.items || []);
      const userList = u.data.items || [];
      setAllUsers(userList);

      // Resolve supervisor in edit mode after allUsers loads
      if (isEdit) {
        usersAPI.get(id).then(r => {
          const usr = r.data;
          const loadedStatus = usr.status || (usr.is_active ? 'activo' : 'inactivo');
          setOriginalStatus(loadedStatus);
          setForm({
            first_name: usr.first_name || '', last_name: usr.last_name || '',
            email: usr.email || '', phone: usr.phone || '',
            role: usr.role || 'levantador', client_id: usr.client_id || '',
            status: loadedStatus,
            temporary_access_expires_at: usr.temporary_access_expires_at?.slice(0, 16) || '',
            password: '',
            emergency_contact: usr.emergency_contact || { name: '', relationship: '', phone: '', email: '' },
            social_security: usr.social_security || { imss_number: '', blood_type: '', allergies: '', medical_notes: '' },
          });
          // Pre-load photo preview if exists
          if (usr.profile_photo) {
            const photoUrl = usr.profile_photo.startsWith('http') 
              ? usr.profile_photo 
              : `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || ''}${usr.profile_photo}`;
            setPhotoPreview(photoUrl);
          }
          // Resolve supervisor from loaded allUsers
          if (usr.direct_supervisor_id) {
            const sup = userList.find(us => us.id === usr.direct_supervisor_id);
            if (sup) { setSelectedSupervisor(sup); setSupervisorSearch(sup.full_name || sup.name || ''); }
          }
        }).catch(() => toast.error('Error al cargar usuario'));
      }
    }).catch(() => {});
  }, [id, isEdit]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error('El nombre es requerido'); return; }
    if (!form.last_name.trim()) { toast.error('Los apellidos son requeridos'); return; }
    if (form.role === 'cliente' && !form.client_id) { toast.error('Debes seleccionar un cliente'); return; }
    setSaving(true);
    try {
      let userId = id;
      const payload = {
        first_name: form.first_name, last_name: form.last_name,
        name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email, phone: form.phone || null,
        role: form.role, client_id: form.client_id || null,
        direct_supervisor_id: selectedSupervisor?.id || null,
        emergency_contact: Object.values(form.emergency_contact).some(Boolean) ? form.emergency_contact : null,
        social_security: Object.values(form.social_security).some(Boolean) ? form.social_security : null,
      };
      if (isEdit) {
        await usersAPI.update(id, payload);
        // #A11 — antes solo se llamaba changeStatus si el status NUEVO
        // no era 'activo', así que reactivar una cuenta suspendida
        // (nuevo status = 'activo') nunca disparaba la llamada: el admin
        // veía "Usuario actualizado" y la cuenta seguía sin poder
        // iniciar sesión. Ahora se compara contra el status ORIGINAL y
        // se envía en cualquier dirección; el error ya no se traga.
        if (form.status !== originalStatus) {
          try {
            await usersAPI.changeStatus(id, {
              status: form.status,
              ...(form.status === 'temporal' && form.temporary_access_expires_at ? { temporary_access_expires_at: form.temporary_access_expires_at } : {})
            });
          } catch {
            toast.error(`El usuario se actualizó, pero no se pudo cambiar su estado a "${STATUS_LABELS[form.status] || form.status}"`);
            return;
          }
        }
        toast.success('Usuario actualizado');
      } else {
        if (!form.password) { toast.error('La contraseña es requerida'); setSaving(false); return; }
        // Crear vía POST /users (admin endpoint). Acepta toda la info en
        // un solo round-trip y respeta send_credentials → welcome email
        // (#jeixrC6i / #ZVAUKJ5l — antes la llamada iba a /auth/register
        // que descartaba en silencio supervisor, emergency, etc.).
        const createPayload = {
          ...payload,
          password: form.password,
          status: form.status || 'activo',
          send_credentials: sendEmail,
          ...(form.status === 'temporal' && form.temporary_access_expires_at
            ? { temporary_access_expires_at: form.temporary_access_expires_at }
            : {}),
        };
        // POST /users devuelve el doc del usuario directo (no envuelto en
        // .user como /auth/register).
        const r = await usersAPI.create(createPayload);
        userId = r.data.id || r.data.user?.id;
        toast.success('Usuario creado');
      }
      // Upload photo after user exists
      if (photoFile && userId) {
        await usersAPI.uploadPhoto(userId, photoFile).catch(() => {});
      }
      navigate(`/dashboard/usuarios/${userId}`);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 80));
    } finally { setSaving(false); }
  };

  const supervisorSearchResults = supervisorSearch.length >= 2
    ? allUsers.filter(u => u.id !== id && (u.name?.toLowerCase().includes(supervisorSearch.toLowerCase()) || u.email?.toLowerCase().includes(supervisorSearch.toLowerCase())))
    : [];

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in" data-testid="user-form">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/usuarios')} className="soem-btn-secondary p-2" aria-label="Volver a usuarios"><ArrowLeft size={18} /></button>
        <h1 className="text-2xl font-bold text-soem-navy">{isEdit ? 'Editar Usuario' : 'Nuevo Usuario'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* S1: Photo */}
        <div className="soem-card p-6">
          <SectionTitle n="1">Foto de perfil</SectionTitle>
          <div className="flex items-center gap-6">
            <div className="relative group cursor-pointer" onClick={() => photoRef.current?.click()}>
              {photoPreview ? (
                <img src={photoPreview} className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg" alt="preview" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center">
                  <Camera size={28} className="text-slate-300" />
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera size={20} className="text-white" />
              </div>
              <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={handlePhotoChange} />
            </div>
            <div>
              <button type="button" onClick={() => photoRef.current?.click()}
                className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2">
                <Camera size={14} /> Seleccionar foto
              </button>
              <p className="text-xs text-slate-400 mt-1">JPG, PNG, WebP · Máx 5MB</p>
            </div>
          </div>
        </div>

        {/* S2: Personal */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="2">Datos personales</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-nombres" className="soem-label block mb-1.5">Nombre(s) *</label>
              <input id="f-nombres" className={inputCls} value={form.first_name} onChange={e => set('first_name', e.target.value)} required data-testid="first-name" />
            </div>
            <div>
              <label htmlFor="f-apellidos" className="soem-label block mb-1.5">Apellidos *</label>
              <input id="f-apellidos" className={inputCls} value={form.last_name} onChange={e => set('last_name', e.target.value)} required data-testid="last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-correo-electrónico" className="soem-label block mb-1.5">Correo electrónico *</label>
              <input id="f-correo-electrónico" type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)}
                required autoComplete="new-email" data-testid="user-email" />
            </div>
            <div>
              <label htmlFor="f-teléfono" className="soem-label block mb-1.5">Teléfono</label>
              <input id="f-teléfono" 
                type="tel" 
                className={inputCls} 
                value={form.phone} 
                onChange={e => set('phone', formatPhoneInput(e.target.value))} 
                placeholder="10 dígitos" 
                maxLength={10}
                autoComplete="off" 
              />
            </div>
          </div>
        </div>

        {/* S3: Access */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="3">Acceso al sistema</SectionTitle>
          <div>
            <label htmlFor="f-rol" className="soem-label block mb-2">Rol</label>
            <div className="grid grid-cols-2 gap-3">
              {ROLES.map(r => (
                <label key={r.value}
                  className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer ${form.role === r.value ? 'border-soem-primary bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input type="radio" name="role" value={r.value} checked={form.role === r.value}
                    onChange={() => setForm(f => ({ ...f, role: r.value, ...(r.value !== 'cliente' ? { client_id: '' } : {}) }))} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-soem-navy">{r.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{r.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {form.role === 'cliente' && (
            <div>
              <label htmlFor="f-empresa-cliente" className="soem-label block mb-1.5">Empresa cliente <span className="text-red-500">*</span></label>
              <select id="f-empresa-cliente" className={inputCls} value={form.client_id} onChange={e => set('client_id', e.target.value)} required>
                <option value="">— Seleccionar empresa —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="f-estado-de-la-cuenta" className="soem-label block mb-1.5">Estado de la cuenta</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s} type="button" onClick={() => set('status', s)}
                  className={`py-2 px-3 rounded-sm border text-xs font-semibold transition-all text-center ${form.status === s ? 'bg-soem-navy text-white border-soem-navy' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
          {form.status === 'temporal' && (
            <div>
              <label htmlFor="f-acceso-válido-hasta" className="soem-label block mb-1.5">Acceso válido hasta *</label>
              <input id="f-acceso-válido-hasta" type="datetime-local" className={inputCls} value={form.temporary_access_expires_at}
                onChange={e => set('temporary_access_expires_at', e.target.value)} required />
            </div>
          )}
          {!isEdit && (
            <div>
              <label htmlFor="f-contraseña" className="soem-label block mb-1.5">Contraseña *</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input id="f-contraseña" type={showPassword ? 'text' : 'password'} className={`${inputCls} pr-10`}
                    value={form.password} onChange={e => set('password', e.target.value)} required
                    autoComplete="new-password"
                    placeholder="Mínimo 8 caracteres" data-testid="user-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <button type="button" onClick={() => set('password', generatePassword())}
                  className="soem-btn-secondary px-3 py-2 text-xs flex items-center gap-1.5" title="Generar contraseña automática">
                  <RefreshCw size={13} /> Auto
                </button>
              </div>
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
                <span className="text-xs text-slate-500">Enviar contraseña por email al usuario</span>
              </label>
            </div>
          )}
        </div>

        {/* S4: Org structure - TAREA 28: Hidden for 'cliente' role */}
        {form.role !== 'cliente' && (
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="4">Estructura organizacional</SectionTitle>
          <div className="relative">
            <label htmlFor="f-jefe-directo" className="soem-label block mb-1.5 flex items-center gap-1"><Search size={11} /> Jefe directo</label>
            <input id="f-jefe-directo" className={inputCls} placeholder="Buscar por nombre o email..."
              value={supervisorSearch}
              onChange={e => { setSupervisorSearch(e.target.value); if (!e.target.value) setSelectedSupervisor(null); }} />
            {supervisorSearchResults.length > 0 && !selectedSupervisor && (
              <div className="absolute z-20 top-full left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg mt-1 max-h-48 overflow-y-auto">
                {supervisorSearchResults.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 cursor-pointer"
                    onClick={() => { setSelectedSupervisor(u); setSupervisorSearch(u.full_name || u.name); }}>
                    <UserAvatar user={u} size="xs" />
                    <div>
                      <p className="text-sm font-semibold text-soem-navy">{u.full_name || u.name}</p>
                      <p className="text-xs text-slate-400">{u.role?.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {selectedSupervisor && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-sm p-3">
              <UserAvatar user={selectedSupervisor} size="sm" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-soem-navy">{selectedSupervisor.full_name || selectedSupervisor.name}</p>
                <p className="text-xs text-slate-500">{selectedSupervisor.role?.replace('_', ' ')}</p>
              </div>
              <button type="button" onClick={() => { setSelectedSupervisor(null); setSupervisorSearch(''); }}
                className="text-slate-400 hover:text-red-500"><span className="text-lg">×</span></button>
            </div>
          )}
        </div>
        )}

        {/* S5: Emergency contact */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="5">Contacto de emergencia</SectionTitle>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-nombre" className="soem-label block mb-1.5">Nombre</label>
              <input id="f-nombre" className={inputCls} value={form.emergency_contact.name} onChange={e => setEC('name', e.target.value)} />
            </div>
            <div>
              <label htmlFor="f-parentesco" className="soem-label block mb-1.5">Parentesco</label>
              <input id="f-parentesco" className={inputCls} value={form.emergency_contact.relationship} onChange={e => setEC('relationship', e.target.value)} placeholder="Ej: Familiar" />
            </div>
            <div>
              <label htmlFor="f-teléfono-1" className="soem-label block mb-1.5">Teléfono</label>
              <input id="f-teléfono-1" 
                type="tel" 
                className={inputCls} 
                value={form.emergency_contact.phone} 
                onChange={e => setEC('phone', formatPhoneInput(e.target.value))} 
                placeholder="10 dígitos"
                maxLength={10}
              />
            </div>
            <div>
              <label htmlFor="f-email-opcional" className="soem-label block mb-1.5">Email (opcional)</label>
              <input id="f-email-opcional" type="email" className={inputCls} value={form.emergency_contact.email || ''} onChange={e => setEC('email', e.target.value)} />
            </div>
          </div>
        </div>

        {/* S6: Medical (collapsible) */}
        <div className="soem-card p-6">
          <SectionTitle n="6" collapsible open={medicalOpen} onToggle={() => setMedicalOpen(!medicalOpen)}>
            Información médica (opcional)
          </SectionTitle>
          {medicalOpen && (
            <div className="space-y-4 mt-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-2 text-xs text-yellow-700 flex items-center gap-1.5">
                ⚠ Información confidencial — solo visible para el administrador y el propio usuario.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="f-número-imss" className="soem-label block mb-1.5">Número IMSS</label>
                  <input id="f-número-imss" className={`${inputCls} font-mono`} value={form.social_security.imss_number}
                    onChange={e => setSS('imss_number', e.target.value)} placeholder="00000000" />
                </div>
                <div>
                  <label htmlFor="f-tipo-de-sangre" className="soem-label block mb-1.5">Tipo de sangre</label>
                  <select id="f-tipo-de-sangre" className={inputCls} value={form.social_security.blood_type} onChange={e => setSS('blood_type', e.target.value)}>
                    <option value="">— Seleccionar —</option>
                    {BLOOD_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label htmlFor="f-alergias" className="soem-label block mb-1.5">Alergias</label>
                <textarea id="f-alergias" className={`${inputCls} resize-none`} rows={2} value={form.social_security.allergies}
                  onChange={e => setSS('allergies', e.target.value)} placeholder="Describe las alergias conocidas..." />
              </div>
              <div>
                <label htmlFor="f-notas-médicas" className="soem-label block mb-1.5">Notas médicas</label>
                <textarea id="f-notas-médicas" className={`${inputCls} resize-none`} rows={2} value={form.social_security.medical_notes}
                  onChange={e => setSS('medical_notes', e.target.value)} placeholder="Condiciones médicas relevantes..." />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button type="submit" disabled={saving}
            className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-user-btn">
            <Save size={15} /> {saving ? 'Guardando...' : (isEdit ? 'Actualizar Usuario' : 'Crear Usuario')}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/usuarios')}
            className="soem-btn-secondary px-8 py-3 text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
